import { useMemo, useState, type FormEvent } from "react";

import { useCreateSavedDashboard } from "../../api/hooks/useCreateSavedDashboard";
import { useSavedDashboards } from "../../api/hooks/useSavedDashboards";
import { useUpdateSavedDashboard } from "../../api/hooks/useUpdateSavedDashboard";
import {
  DEFAULT_DASHBOARD_ID,
  DEFAULT_DASHBOARD_NAME,
} from "./CustomDashboardSelect";
import { writeLayoutToBlob } from "./widgets/layoutAdapter";
import type { DashboardLayout } from "./widgets/types";

export type SaveLayoutButtonProps = {
  layout: DashboardLayout;
  /** Called after a successful POST or PATCH with the saved row's
   * `saved_dashboard_id`, so the page can update the picker selection
   * to point at the just-saved layout. */
  onSaved?: (savedDashboardId: string) => void;
};

export function SaveLayoutButton({ layout, onSaved }: SaveLayoutButtonProps) {
  const createMutation = useCreateSavedDashboard();
  const updateMutation = useUpdateSavedDashboard();
  const { data, isLoading: isLoadingList } = useSavedDashboards();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const existing = useMemo(
    () =>
      // Drop the backend's synthetic sentinel ("Dashboard (default)" with
      // the all-zeros UUID) so it doesn't appear as a chip and so a typed
      // name matching it never accidentally PATCHes the server's
      // read-only synthetic row.
      (data?.items ?? []).filter(
        (item) =>
          !item.is_deleted &&
          item.saved_dashboard_id !== DEFAULT_DASHBOARD_ID,
      ),
    [data],
  );

  const trimmed = name.trim();
  const isReservedName =
    trimmed.toLowerCase() === DEFAULT_DASHBOARD_NAME.toLowerCase();
  const match = useMemo(
    () =>
      existing.find(
        (item) => item.name.toLowerCase() === trimmed.toLowerCase(),
      ),
    [existing, trimmed],
  );

  const isPending = createMutation.isPending || updateMutation.isPending;
  const isError = createMutation.isError || updateMutation.isError;

  const reset = () => {
    setName("");
    setOpen(false);
    createMutation.reset();
    updateMutation.reset();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (trimmed.length === 0 || isReservedName) return;
    const blob = writeLayoutToBlob(layout);
    if (match) {
      updateMutation.mutate(
        {
          saved_dashboard_id: match.saved_dashboard_id,
          body: { name: match.name, layout: blob },
        },
        {
          onSuccess: (saved) => {
            onSaved?.(saved.saved_dashboard_id);
            reset();
          },
        },
      );
    } else {
      createMutation.mutate(
        { name: trimmed, layout: blob },
        {
          onSuccess: (saved) => {
            onSaved?.(saved.saved_dashboard_id);
            reset();
          },
        },
      );
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-control border border-outline-variant bg-surface px-3 py-1.5 text-sm font-medium text-on-surface transition-colors hover:bg-surface-container-high"
      >
        Save layout…
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="Save dashboard layout"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        >
          <form
            onSubmit={handleSubmit}
            className="w-full max-w-md rounded-control border border-outline-variant bg-surface p-4 shadow-lg"
          >
            <h2 className="font-display text-lg font-medium text-on-surface">
              Save layout
            </h2>
            <p className="mt-1 text-sm text-on-surface-variant">
              Type a new name to save as new, or pick an existing layout to
              overwrite.
            </p>
            <label
              htmlFor="save-layout-name"
              className="mt-3 block text-sm font-medium text-on-surface"
            >
              Name
            </label>
            <input
              id="save-layout-name"
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoFocus
              required
              maxLength={200}
              className="mt-1 w-full rounded-control border border-outline-variant bg-surface px-3 py-1.5 text-sm text-on-surface"
            />
            {existing.length > 0 && (
              <fieldset className="mt-3">
                <legend className="mb-1 text-xs font-medium uppercase tracking-wide text-on-surface-variant">
                  Existing layouts
                </legend>
                <div className="flex flex-wrap gap-1">
                  {existing.map((item) => {
                    const isPicked =
                      trimmed.toLowerCase() === item.name.toLowerCase();
                    return (
                      <button
                        key={item.saved_dashboard_id}
                        type="button"
                        aria-pressed={isPicked}
                        onClick={() => {
                          setName(item.name);
                        }}
                        className={`rounded-control border px-2 py-1 text-xs transition-colors ${
                          isPicked
                            ? "border-primary bg-surface-container-high text-on-surface"
                            : "border-outline-variant bg-surface text-on-surface-variant hover:bg-surface-container-high"
                        }`}
                      >
                        {item.name}
                      </button>
                    );
                  })}
                </div>
              </fieldset>
            )}
            {isReservedName && (
              <p className="mt-3 text-sm text-danger" role="alert">
                &lsquo;{DEFAULT_DASHBOARD_NAME}&rsquo; is the built-in layout
                and can&apos;t be overwritten. Pick a different name.
              </p>
            )}
            {!isReservedName && match && (
              <p className="mt-3 text-sm text-on-surface-variant" role="note">
                This will overwrite the existing layout &lsquo;{match.name}&rsquo;.
              </p>
            )}
            {isError ? (
              <p className="mt-2 text-sm text-danger" role="alert">
                {match
                  ? "Failed to update layout. Please try again."
                  : "Failed to save layout. Please try again."}
              </p>
            ) : null}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={reset}
                className="rounded-control border border-outline-variant bg-surface px-3 py-1.5 text-sm font-medium text-on-surface transition-colors hover:bg-surface-container-high"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  isPending ||
                  trimmed.length === 0 ||
                  isLoadingList ||
                  isReservedName
                }
                className="rounded-control bg-primary px-3 py-1.5 text-sm font-medium text-on-primary transition-colors hover:bg-primary-hover disabled:opacity-60"
              >
                {isPending
                  ? match
                    ? "Updating…"
                    : "Saving…"
                  : match
                    ? "Overwrite"
                    : "Save"}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </>
  );
}
